import {setLocale as setVeeLocale} from "@vee-validate/i18n"
import {defineStore} from 'pinia'
import type {ServiceProviderResource} from "~/types/resources/organisation"

type AuthDataResponse = {
    abilities: Array<{ subject: string, action: string }>,
    impersonate: boolean,
    subscription: Record<string, unknown>,
    userData: {
        id: number,
        firstname: string,
        lastname: string,
        fullname: string,
        locale: 'en' | 'de',
        organisation_name: string | null,
        organisation_id: number,
        organisation_token: string | null,
        is_service_provider: boolean,
        serviceProvider: ServiceProviderResource | null,
        active_project_id: number | null,
        email: string,
        image_url: string | null,
        verified: boolean
    }
}

type AuthData = {
    abilities: AuthDataResponse['abilities'],
    userData: AuthDataResponse['userData'] | null,
    impersonate: AuthDataResponse['impersonate'],
    isAuthenticated: boolean | null,
    remoteVersion: string | null,
    redirectPathAfterLogin: string | null,
    subscription: Record<string, unknown>,
    serviceProvider: ServiceProviderResource | null,
    shake: boolean
}

export const useAuthStore = defineStore('auth', {
    // convert to a function
    state: (): AuthData => ({
        isAuthenticated: null,
        abilities: [],
        userData: {
            id: 0,
            firstname: '',
            lastname: '',
            fullname: '',
            locale: 'de',
            organisation_name: null,
            organisation_id: 0,
            organisation_token: '',
            is_service_provider: false,
            serviceProvider: null,
            active_project_id: null,
            email: '',
            image_url: null,
            verified: false,
        },
        subscription: {},
        shake: false,
        remoteVersion: null,
        serviceProvider: null,
        impersonate: false,
        redirectPathAfterLogin: null
    }),

    actions: {
        //https://pinia.vuejs.org/core-concepts/actions.html#subscribing-to-actions
        async successfulLogin(redirectPath = '/dashboard', query = {}) {
            return await new Promise((resolve, reject) => {
                try {
                    this.isAuthenticated = true
                    this.getAuthData().then(() => {
                        if (this.redirectPathAfterLogin) {
                            redirectPath = this.redirectPathAfterLogin
                            this.redirectPathAfterLogin = null
                        }
                        navigateTo({
                            path: redirectPath,
                            query: query
                        }, {external: true})
                        resolve(true)
                    })
                } catch (error) {
                    reject(error)
                }
            })
        },

        async getAuthData() {
            const response = await $lara.get<AuthDataResponse>(useNuxtApp().$apiRoute('spa.authData'))
            if (response.error) {
                return
            }
            this.setAuthData(response)
        },

        setAuthData(data: AuthDataResponse) {
            this.isAuthenticated = true
            this.abilities = data.abilities
            this.userData = data.userData
            this.subscription = data.subscription
            this.impersonate = data.impersonate

            if (this.userData?.locale) {
                const lang = useLocalStorage<'de' | 'en'>('lang', this.userData?.locale)
                lang.value = this.userData?.locale
                useNuxtApp().$i18n.setLocale(lang.value)
                setVeeLocale(lang.value)
                $lara.put(useNuxtApp().$apiRoute('spa.settings.locale'), {body: {locale: lang.value}})
            }

            if (this.userData?.serviceProvider) {
                localStorage.setItem('serviceProvider', JSON.stringify(this.userData.serviceProvider))
            } else {
                localStorage.removeItem('serviceProvider')
            }

            localStorage.setItem('isAuthenticated', 'true')
            localStorage.setItem('abilities', JSON.stringify(data.abilities))
            localStorage.setItem('userData', JSON.stringify(data.userData))
        },

        setRemoteVersion(version: string) {
            this.remoteVersion = version
        },

        resetAuthData() {
            this.isAuthenticated = false
            this.abilities = []
            this.userData = null
            this.impersonate = false

            localStorage.setItem('isAuthenticated', 'null')
            localStorage.setItem('abilities', 'null')
            localStorage.setItem('userData', 'null')
        },

        async logout(redirectPath = '/auth') {
            await $lara.post('/app/spa/logout')
            this.resetAuthData()
            navigateTo({
                path: redirectPath
            })
        },

        initialize() {
            const cachedServiceProvider = localStorage.getItem('serviceProvider')
            if (cachedServiceProvider)
                this.serviceProvider = JSON.parse(cachedServiceProvider as string)
        }
    }
})
